import Dexie, { type EntityTable, type Table } from 'dexie';
import type {
  CareerProgress,
  CloudDatasetSummary,
  ImportResult,
  ImportSource,
  NormalizedRunV2,
  SavedView,
  Settings
} from '../domain/types';

type StoredRun = NormalizedRunV2 & { sourceId: string; digest?: string; modifiedAt?: number; size?: number };
type RawRun = { id: string; sourceId: string; fileName: string; raw: Record<string, unknown>; digest?: string };
type SourceRecord = ImportSource & { lastRefresh: number; fileManifest: Record<string, string>; pendingMissing: string[] };
type SettingRecord = { key: string; value: unknown; updatedAt: number };
type ProgressRecord = { id: 'career'; value: CareerProgress; updatedAt: number };
type ImportRecord = { id?: number; sourceId: string; at: number; result: Omit<ImportResult, 'runs' | 'progress'> };
type HandleRecord = { id: string; handle: FileSystemDirectoryHandle };
export type DatasetSyncState = {
  ownerId: string;
  datasetId: string;
  importAt: number;
  syncedAt: string;
  runManifest?: Record<string, string>;
  localToCloudIds?: Record<string, string>;
  progressDigest?: string;
  revision?: number;
};

const DEFAULT_SETTINGS: Settings = {
  ascensionScope: 'all',
  abandonPolicy: 'include',
  shortAbandonMinutes: 10,
  cardMinSeen: 5,
  encounterMinFights: 1,
  relicMinRuns: 1,
  trendWindow: 10,
  comboMinRuns: 8
};

export class StsDatabase extends Dexie {
  runs!: EntityTable<StoredRun, 'id'>;
  rawRuns!: EntityTable<RawRun, 'id'>;
  progress!: EntityTable<ProgressRecord, 'id'>;
  imports!: EntityTable<ImportRecord, 'id'>;
  sources!: EntityTable<SourceRecord, 'id'>;
  settings!: EntityTable<SettingRecord, 'key'>;
  savedViews!: EntityTable<SavedView, 'id'>;
  handles!: EntityTable<HandleRecord, 'id'>;
  state!: Table<unknown, string>;

  constructor(name = 'sts2stats-local-clone') {
    super(name);
    this.version(1).stores({ state: '' });
    this.version(2).stores({
      state: '',
      runs: 'id,sourceId,sourceScope,startTime,character,buildId,ascension,status,isMultiplayer',
      rawRuns: 'id,sourceId,fileName',
      progress: 'id,updatedAt',
      imports: '++id,sourceId,at',
      sources: 'id,kind,lastRefresh',
      settings: 'key,updatedAt',
      savedViews: 'id,updatedAt,name',
      handles: 'id'
    }).upgrade(async (transaction) => {
      const legacy = transaction.table('state');
      const legacyRuns = await legacy.get('runs') as NormalizedRunV2[] | undefined;
      const legacyShared = await legacy.get('sharedRuns') as NormalizedRunV2[] | undefined;
      const legacyProgress = await legacy.get('progress') as CareerProgress | undefined;
      const legacyHandle = await legacy.get('directoryHandle') as FileSystemDirectoryHandle | undefined;
      const normalizeLegacy = (run: NormalizedRunV2, scope: 'history' | 'imported'): StoredRun => {
        const { raw: _raw, ...normalized } = run;
        return ({
        ...normalized,
        modelVersion: 2,
        parserVersion: run.parserVersion || '1-migrated',
        sourceScope: scope,
        sourceKey: run.sourceKey || run.fileName || run.id,
        importedAt: run.importedAt || Date.now(),
        capabilities: run.capabilities || {
          hasMap: Boolean(run.map?.length),
          hasPlayerTelemetry: Boolean(run.timeline?.length),
          hasCardChoices: Boolean(run.cardChoices?.length),
          hasRelicEvents: Boolean(run.relicEvents?.length),
          hasEncounterEvents: Boolean(run.encounterEvents?.length),
          hasVersion: Boolean(run.buildId)
        },
        sourceId: scope === 'history' ? 'legacy-local' : 'legacy-imported'
      } as unknown as StoredRun); };
      if (Array.isArray(legacyRuns) && legacyRuns.length) await transaction.table('runs').bulkPut(legacyRuns.map((run) => normalizeLegacy(run, 'history')));
      if (Array.isArray(legacyShared) && legacyShared.length) await transaction.table('runs').bulkPut(legacyShared.map((run) => normalizeLegacy(run, 'imported')));
      const allLegacy = [...(legacyRuns || []).map((run) => ({ run, sourceId: 'legacy-local' })), ...(legacyShared || []).map((run) => ({ run, sourceId: 'legacy-imported' }))];
      if (allLegacy.length) await transaction.table('rawRuns').bulkPut(allLegacy.map(({ run, sourceId }) => ({ id: run.id, sourceId, fileName: run.fileName, raw: run.raw || {} })));
      if (legacyProgress) await transaction.table('progress').put({ id: 'career', value: legacyProgress, updatedAt: Date.now() });
      if (legacyHandle) await transaction.table('handles').put({ id: 'primary', handle: legacyHandle });
      await transaction.table('settings').put({
        key: 'migration',
        value: { completedAt: Date.now(), legacyRuns: legacyRuns?.length || 0, legacySharedRuns: legacyShared?.length || 0 },
        updatedAt: Date.now()
      });
    });
    this.version(3).stores({
      state: '',
      runs: 'id,sourceId,sourceScope,startTime,character,buildId,ascension,status,isMultiplayer',
      rawRuns: 'id,sourceId,fileName',
      progress: 'id,updatedAt',
      imports: '++id,sourceId,at',
      sources: 'id,kind,lastRefresh',
      settings: 'key,updatedAt',
      savedViews: 'id,updatedAt,name',
      handles: 'id'
    }).upgrade(async (transaction) => {
      await Promise.all([
        transaction.table('runs').clear(),
        transaction.table('rawRuns').clear(),
        transaction.table('progress').clear(),
        transaction.table('imports').clear(),
        transaction.table('sources').clear()
      ]);
      await transaction.table('settings').put({
        key: 'singleDatasetMode',
        value: { enabledAt: Date.now(), requiresReimport: true },
        updatedAt: Date.now()
      });
    });
  }
}

export const db = new StsDatabase();

export interface RunRepository {
  list(scope?: 'history' | 'imported' | 'all'): Promise<NormalizedRunV2[]>;
  get(id: string): Promise<NormalizedRunV2 | undefined>;
  commitImport(source: ImportSource, result: ImportResult, manifest: Record<string, string>, ownerId?: string | null): Promise<void>;
  commitIncremental(source: ImportSource, result: ImportResult, manifest: Record<string, string>, removedRunIds: string[], progressChanged: boolean, ownerId?: string | null): Promise<void>;
  cacheCloudDataset(runs: NormalizedRunV2[], progress: CareerProgress | null, ownerId: string, dataset: CloudDatasetSummary): Promise<void>;
  clear(scope?: 'history' | 'imported' | 'all'): Promise<void>;
}

export interface SettingsRepository {
  get(): Promise<Settings>;
  save(settings: Settings): Promise<void>;
}

export interface SavedViewRepository {
  list(): Promise<SavedView[]>;
  save(view: SavedView): Promise<void>;
  delete(id: string): Promise<void>;
}

export const runRepository: RunRepository = {
  async list(scope = 'all') {
    const records = scope === 'all'
      ? await db.runs.orderBy('startTime').reverse().toArray()
      : (await db.runs.where('sourceScope').equals(scope).toArray()).sort((a, b) => b.startTime - a.startTime);
    return records.map((run) => ({ ...run, raw: {} })) as unknown as NormalizedRunV2[];
  },
  async get(id) {
    const [run, raw] = await Promise.all([db.runs.get(id), db.rawRuns.get(id)]);
    return run ? ({ ...run, raw: raw?.raw || {} } as unknown as NormalizedRunV2) : undefined;
  },
  async commitImport(source, result, manifest, ownerId = null) {
    await db.transaction('rw', [db.runs, db.rawRuns, db.progress, db.imports, db.sources, db.settings], async () => {
      const scope = source.kind === 'demo' ? 'demo' : 'history';
      const records: StoredRun[] = result.runs.map((run) => {
        const { raw: _raw, ...normalized } = run;
        return { ...normalized, sourceId: source.id, sourceScope: scope } as unknown as StoredRun;
      });
      await Promise.all([db.runs.clear(), db.rawRuns.clear(), db.progress.clear(), db.imports.clear(), db.sources.clear()]);
      if (records.length) {
        await db.runs.bulkPut(records);
        await db.rawRuns.bulkPut(result.runs.map((run) => ({ id: run.id, sourceId: source.id, fileName: run.fileName, raw: run.raw })));
      }
      if (result.progress) await db.progress.put({ id: 'career', value: result.progress, updatedAt: Date.now() });
      await db.sources.put({ ...source, lastRefresh: Date.now(), fileManifest: manifest, pendingMissing: [] });
      await db.imports.add({
        sourceId: source.id,
        at: Date.now(),
        result: {
          failures: result.failures,
          warnings: result.warnings,
          coverage: result.coverage,
          changes: result.changes,
          parserVersion: result.parserVersion,
          importedAt: result.importedAt
        }
      });
      await db.settings.delete('datasetSync');
      await db.settings.put({ key: 'datasetOwner', value: { userId: ownerId }, updatedAt: Date.now() });
    });
  },
  async commitIncremental(source, result, manifest, removedRunIds, progressChanged, ownerId = null) {
    await db.transaction('rw', [db.runs, db.rawRuns, db.progress, db.imports, db.sources, db.settings], async () => {
      const scope = source.kind === 'demo' ? 'demo' : 'history';
      const records: StoredRun[] = result.runs.map((run) => {
        const { raw: _raw, ...normalized } = run;
        return { ...normalized, sourceId: source.id, sourceScope: scope } as unknown as StoredRun;
      });
      if (removedRunIds.length) {
        await db.runs.bulkDelete(removedRunIds);
        await db.rawRuns.bulkDelete(removedRunIds);
      }
      if (records.length) {
        await db.runs.bulkPut(records);
        await db.rawRuns.bulkPut(result.runs.map((run) => ({ id: run.id, sourceId: source.id, fileName: run.fileName, raw: run.raw })));
      }
      if (progressChanged) {
        await db.progress.clear();
        if (result.progress) await db.progress.put({ id: 'career', value: result.progress, updatedAt: Date.now() });
      }
      await db.sources.put({ ...source, lastRefresh: Date.now(), fileManifest: manifest, pendingMissing: [] });
      await db.imports.add({
        sourceId: source.id,
        at: Date.now(),
        result: {
          failures: result.failures,
          warnings: result.warnings,
          coverage: result.coverage,
          changes: result.changes,
          parserVersion: result.parserVersion,
          importedAt: result.importedAt,
          delta: result.delta
        }
      });
      await db.settings.put({ key: 'datasetOwner', value: { userId: ownerId }, updatedAt: Date.now() });
    });
  },
  async cacheCloudDataset(runs, progress, ownerId, dataset) {
    await db.transaction('rw', [db.runs, db.rawRuns, db.progress, db.imports, db.sources, db.settings], async () => {
      const records = runs.map(({ raw: _raw, ...run }) => ({ ...run, sourceId: 'cloud-cache', sourceScope: 'history' as const } as unknown as StoredRun));
      await Promise.all([db.runs.clear(), db.rawRuns.clear(), db.progress.clear(), db.imports.clear(), db.sources.clear()]);
      if (records.length) await db.runs.bulkPut(records);
      if (progress) await db.progress.put({ id: 'career', value: progress, updatedAt: Date.now() });
      await db.sources.put({ kind: 'single', id: 'cloud-cache', label: '云端存档缓存', lastRefresh: Date.now(), fileManifest: {}, pendingMissing: [] });
      await db.settings.put({ key: 'datasetOwner', value: { userId: ownerId }, updatedAt: Date.now() });
      await db.settings.put({
        key: 'datasetSync',
        value: { ownerId, datasetId: dataset.id, importAt: 0, syncedAt: dataset.uploadedAt, revision: dataset.revision } satisfies DatasetSyncState,
        updatedAt: Date.now()
      });
    });
  },
  async clear(scope = 'all') {
    if (scope === 'all') {
      await db.transaction('rw', [db.runs, db.rawRuns, db.progress, db.imports, db.sources], async () => {
        await Promise.all([db.runs.clear(), db.rawRuns.clear(), db.progress.clear(), db.imports.clear(), db.sources.clear()]);
      });
      return;
    }
    const ids = await db.runs.where('sourceScope').equals(scope).primaryKeys();
    await db.transaction('rw', [db.runs, db.rawRuns], async () => {
      await db.runs.bulkDelete(ids);
      await db.rawRuns.bulkDelete(ids);
    });
  }
};

export const settingsRepository: SettingsRepository = {
  async get() {
    const record = await db.settings.get('user');
    let legacy: Partial<Settings> = {};
    try { legacy = JSON.parse(window.localStorage.getItem('sts2stats.userSettings') || '{}'); } catch { /* no-op */ }
    return { ...DEFAULT_SETTINGS, ...legacy, ...(record?.value as Partial<Settings> || {}) };
  },
  async save(settings) {
    await db.settings.put({ key: 'user', value: settings, updatedAt: Date.now() });
  }
};

export const savedViewRepository: SavedViewRepository = {
  list: () => db.savedViews.orderBy('updatedAt').reverse().toArray(),
  save: (view) => db.savedViews.put(view).then(() => undefined),
  delete: (id) => db.savedViews.delete(id)
};

export async function getCareerProgress(): Promise<CareerProgress | null> {
  return (await db.progress.get('career'))?.value || null;
}

export async function saveDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  await db.handles.put({ id: 'primary', handle });
}

export async function loadDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
  return (await db.handles.get('primary'))?.handle || null;
}

export async function getSource(id: string): Promise<SourceRecord | undefined> {
  return db.sources.get(id);
}

export async function getLatestImport(): Promise<ImportRecord | undefined> {
  return db.imports.orderBy('at').last();
}

export async function getDatasetOwnerId(): Promise<string | null> {
  const value = (await db.settings.get('datasetOwner'))?.value as { userId?: unknown } | undefined;
  return typeof value?.userId === 'string' ? value.userId : null;
}

export async function getDatasetSyncState(): Promise<DatasetSyncState | null> {
  const value = (await db.settings.get('datasetSync'))?.value as Partial<DatasetSyncState> | undefined;
  return typeof value?.ownerId === 'string' && typeof value.datasetId === 'string' && typeof value.importAt === 'number' && typeof value.syncedAt === 'string'
    ? value as DatasetSyncState
    : null;
}

export async function saveDatasetSyncState(ownerId: string, dataset: CloudDatasetSummary, importAt: number, metadata: Pick<DatasetSyncState, 'runManifest' | 'localToCloudIds' | 'progressDigest'> = {}): Promise<void> {
  await db.settings.put({
    key: 'datasetSync',
    value: { ownerId, datasetId: dataset.id, importAt, syncedAt: dataset.uploadedAt, revision: dataset.revision, ...metadata } satisfies DatasetSyncState,
    updatedAt: Date.now()
  });
}
